import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, AlertCircle, Loader2, ArrowRight, ArrowLeft } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "pending", payment_status: "pending", plan: "free" });
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!sessionId) {
      setError("Falta el session_id en la URL");
      return;
    }

    let cancelled = false;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        setState(data);
        if (data.payment_status === "paid" || data.status === "complete") {
          await refresh();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setError("El pago no se completó.");
          return;
        }
        if (n >= MAX_ATTEMPTS) {
          setError("Aún no recibimos confirmación de Stripe. Revisa tu correo o intenta más tarde.");
          return;
        }
        setAttempts(n + 1);
        setTimeout(() => poll(n + 1), POLL_MS);
      } catch (e) {
        setError(e?.response?.data?.detail || "Error consultando el estado del pago");
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const paid = state.payment_status === "paid";

  return (
    <div className="min-h-screen bg-paper text-ink grid place-items-center px-6" data-testid="billing-success-page">
      <div className="max-w-md w-full">
        <Link
          to="/dashboard"
          data-testid="back-to-dashboard"
          className="inline-flex items-center gap-1.5 text-[12px] text-ink-soft hover:text-forest mb-6 transition-colors"
        >
          <ArrowLeft size={13} /> Volver al escritorio
        </Link>
        <Link to="/" className="inline-flex items-baseline gap-0.5">
          <span className="font-serif italic text-2xl text-copper">una</span><span className="font-serif text-2xl text-ink">TESIS</span>
        </Link>

        <div className="mt-10 rounded-2xl border border-rule bg-white p-8 text-center">
          {error ? (
            <>
              <span className="grid place-items-center h-12 w-12 rounded-full bg-[#FBE9E5] text-[#B6533F] mx-auto">
                <AlertCircle size={20} />
              </span>
              <h1 className="mt-5 font-serif text-2xl text-ink">No pudimos confirmar tu pago</h1>
              <p className="mt-2 text-[14px] text-ink-soft" data-testid="billing-error">{error}</p>
            </>
          ) : paid ? (
            <>
              <span className="grid place-items-center h-12 w-12 rounded-full bg-forest text-paper mx-auto">
                <CheckCircle2 size={22} />
              </span>
              <h1 className="mt-5 font-serif text-2xl text-ink">¡Bienvenido a Premium!</h1>
              <p className="mt-2 text-[14px] text-ink-soft" data-testid="billing-success-msg">
                Acabas de desbloquear generaciones ilimitadas, subida de PDFs y plantillas universitarias.
              </p>
            </>
          ) : (
            <>
              <span className="grid place-items-center h-12 w-12 rounded-full bg-paper-2 text-forest mx-auto">
                <Loader2 size={22} className="animate-spin" />
              </span>
              <h1 className="mt-5 font-serif text-2xl text-ink">Procesando tu pago…</h1>
              <p className="mt-2 text-[14px] text-ink-soft" data-testid="billing-pending">
                Confirmando con Stripe. Esto suele tardar pocos segundos. ({attempts}/{MAX_ATTEMPTS})
              </p>
            </>
          )}

          {(paid || error) && (
            <button
              data-testid="billing-continue-btn"
              onClick={() => navigate("/dashboard")}
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all"
            >
              Ir a mi escritorio
              <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
